import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { isAdminUser, isDeptManager, can } from "@/v2/lib/rbac";
import "@/v2/v2.css";
import "@/v2/haramain.css";
import "@/v2/official.css";

// ---------- Official platform (V2) ----------
import V2Login from "@/v2/pages/Login";
import V2Register from "@/v2/pages/Register";
import V2Onboarding from "@/v2/pages/Onboarding";
import V2Pending from "@/v2/pages/Pending";
import V2Layout from "@/v2/V2Layout";
import V2Dashboard from "@/v2/pages/Dashboard";
import V2Journey from "@/v2/pages/Journey";
import V2ChallengeDetail from "@/v2/pages/ChallengeDetail";
import V2Profile from "@/v2/pages/Profile";
import V2Notifications from "@/v2/pages/Notifications";

// ---------- Department Manager (مدير إدارة) ----------

// ---------- Admin Control Center ----------
import AdminShell from "@/v2/admin/AdminShell";
import VideoAISettings from "@/v2/admin/VideoAISettings";
import AdminOverview from "@/v2/admin/Overview";
import HaramainMap from "@/v2/admin/HaramainMap";
import { PeopleHub, ContentHub, PerformanceHub } from "@/v2/admin/ControlHub";
import AdminAudit from "@/v2/admin/Audit";

// ---------- منصة مسار (إدارة الجدارات) ----------
import MasarHome from "@/masar/pages/Home";
import MasarPlan from "@/masar/pages/Plan";
import MasarCertificates from "@/masar/pages/Certificates";
import MasarCertGuide from "@/masar/pages/CertGuide";
import MasarOffers from "@/masar/pages/Offers";
import MasarAdminShell from "@/masar/admin/MasarAdminShell";
import MasarOverview from "@/masar/admin/Overview";
import MasarReports from "@/masar/admin/Reports";
import MasarStructure from "@/masar/admin/Structure";
import MasarMatrix from "@/masar/admin/Matrix";
import MasarCertsAdmin from "@/masar/admin/CertsAdmin";
import MasarUsers from "@/masar/admin/UsersAdmin";

const Loader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#25343f]">
    <div className="w-12 h-12 rounded-full border-4 border-white/10 border-t-[#bb9661] animate-spin" />
  </div>
);

// الوجهة الأولى بعد الدخول: مسار لكل الموظفين (بما فيهم مديرو الإدارات)، ومركز التحكم للمسؤولين
const homeFor = user => user.needs_onboarding ? "/onboarding" : isAdminUser(user) ? "/admin" : "/masar";

/** بوابة الدخول للمحتوى: الإعداد الأولي ثم الاعتماد ثم المنصة */
const Protected = ({ children, adminOnly }) => {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  if (loading) return <Loader />;
  if (!user) {
    if (pathname.startsWith("/journey/")) sessionStorage.setItem("pending-program", pathname);
    return <Navigate to="/login" replace />;
  }
  if (user.needs_onboarding) return <Navigate to="/onboarding" replace />;
  if (["rejected", "suspended"].includes(user.approval_status)) return <Navigate to="/pending" replace />;
  if (user.is_approved === false) return <Navigate to="/pending" replace />;
  if (adminOnly && !can(user, "view_overview")) return <Navigate to="/dashboard" replace />;
  if (isDeptManager(user) && /^\/masar\/admin\/(matrix|structure|qualifications|certificates)/.test(pathname)) return <Navigate to="/masar/admin" replace />;
  return children;
};

const Shell = () => (<Protected><V2Layout /></Protected>);

const Perm = ({ perm, children }) => {
  const { user } = useAuth();
  return can(user, perm) ? children : <Navigate to="/admin" replace />;
};

const LegacyRedirect = () => {
  const { pathname } = useLocation();
  return <Navigate to={pathname.replace(/^\/v2/, "") || "/dashboard"} replace />;
};

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={homeFor(user)} replace /> : <V2Login />} />
      <Route path="/register" element={user ? <Navigate to={homeFor(user)} replace /> : <V2Register />} />
      <Route path="/onboarding" element={
        !user ? <Navigate to="/login" replace />
          : user.needs_onboarding ? <V2Onboarding />
            : user.is_approved ? <Navigate to={isAdminUser(user) ? "/admin" : "/masar/plan"} replace /> : <Navigate to="/pending" replace />} />
      <Route path="/pending" element={
        !user ? <Navigate to="/login" replace />
          : user.needs_onboarding ? <Navigate to="/onboarding" replace />
            : user.is_approved ? <Navigate to={homeFor(user)} replace /> : <V2Pending />} />

      {/* الدخول المباشر إلى المنصة مع الاحتفاظ ببوابات الحساب الحالية */}
      <Route path="/" element={<Navigate to={user ? homeFor(user) : "/login"} replace />} />

      <Route path="/masar/assessment" element={<Navigate to="/masar" replace />} />
      <Route element={<Shell />}>
        <Route path="/dashboard" element={<V2Dashboard />} />
        <Route path="/journey" element={<V2Journey />} />
        <Route path="/journey/:id" element={<V2ChallengeDetail />} />
        <Route path="/notifications" element={<V2Notifications />} />
        <Route path="/profile" element={<V2Profile />} />

        {/* منصة مسار — نفس القالب والجلسة (بلا إشعارات ولا نبض تعلّم) */}
        <Route path="/masar" element={<MasarHome />} />
        <Route path="/masar/plan" element={<MasarPlan />} />
        <Route path="/masar/path" element={<Navigate to="/masar/plan" replace />} />
        <Route path="/masar/certificates" element={<MasarCertificates />} />
        <Route path="/masar/certificates/guide" element={<MasarCertGuide />} />
        <Route path="/masar/offers" element={<MasarOffers />} />
        <Route path="/masar/notifications" element={<Navigate to="/masar" replace />} />
        <Route path="/masar/admin" element={<MasarAdminShell />}>
          <Route index element={<MasarOverview />} />
          <Route path="reports" element={<MasarReports />} />
          <Route path="structure" element={<MasarStructure />} />
          <Route path="matrix" element={<MasarMatrix />} />
          <Route path="programs" element={<Navigate to="/masar/admin" replace />} />
          <Route path="certificates" element={<MasarCertsAdmin />} />
          <Route path="qualifications" element={<MasarCertsAdmin catalogOnly />} />
          <Route path="users" element={<MasarUsers />} />
          <Route path="grants" element={<Navigate to="/admin/people/employees" replace />} />
        </Route>
        <Route path="/masar/*" element={<Navigate to="/masar" replace />} />

        <Route path="/dept" element={<Navigate to="/admin" replace />} />
        <Route path="/dept/employees" element={<Navigate to="/admin/people/employees" replace />} />
        <Route path="/dept/analytics" element={<Navigate to="/admin/performance/analytics" replace />} />
        <Route path="/dept/reports" element={<Navigate to="/admin/performance/reports" replace />} />

        <Route path="/admin" element={<Protected adminOnly><AdminShell /></Protected>}>
          <Route index element={<AdminOverview />} />
          <Route path="haramain-map" element={<Perm perm="view_users"><HaramainMap /></Perm>} />
          <Route path="ai-settings" element={<Perm perm="manage_api"><VideoAISettings /></Perm>} />
          <Route path="people" element={<Perm perm="view_users"><PeopleHub /></Perm>} />
          <Route path="people/:section" element={<Perm perm="view_users"><PeopleHub /></Perm>} />
          <Route path="content" element={<Perm perm="manage_content"><ContentHub /></Perm>} />
          <Route path="content/:section" element={<Perm perm="manage_content"><ContentHub /></Perm>} />
          <Route path="performance" element={<PerformanceHub />} />
          <Route path="performance/:section" element={<PerformanceHub />} />
          <Route path="audit" element={<Perm perm="view_audit"><AdminAudit /></Perm>} />

          {/* الروابط القديمة تبقى صالحة وتنتقل إلى مركزها الجديد */}
          <Route path="join-requests" element={<Navigate to="/admin/people/requests" replace />} />
          <Route path="employees" element={<Navigate to="/admin/people/employees" replace />} />
          <Route path="departments" element={<Navigate to="/admin/people/departments" replace />} />
          <Route path="competencies" element={<Navigate to="/admin/content/courses" replace />} />
          <Route path="paths" element={<Navigate to="/admin/content/courses" replace />} />
          <Route path="challenges" element={<Navigate to="/admin/content/courses" replace />} />
          <Route path="videos" element={<Navigate to="/admin/content/courses" replace />} />
          <Route path="files" element={<Navigate to="/admin/content/courses" replace />} />
          <Route path="quizzes" element={<Navigate to="/admin/content/courses" replace />} />
          <Route path="analytics" element={<Navigate to="/admin/performance/analytics" replace />} />
          <Route path="reports" element={<Navigate to="/admin/performance/reports" replace />} />
        </Route>
      </Route>

      {/* old V2 links keep working */}
      <Route path="/v2/*" element={<LegacyRedirect />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    document.documentElement.dir = "rtl";
    document.documentElement.lang = "ar";
  }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors dir="rtl" />
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
